import moment from 'moment-timezone';
import { ChargePoint, ChargePointSummary } from '../../../app/ApiGen';
import { getChargePointConnectivityStatus } from '../../../utils/chargePoints/getChargePointConnectivityStatus';
import { organisationHasAttributes } from '../../../utils/format';
import { Normalised } from '../../../utils/request';
import { ChargePointState } from '../Commands/types';
import { getLocalLoadManagementMode } from '../../../utils/chargePoints/getLocalLoadManagementMode';

export type TableChargePoint = Pick<
  Normalised<ChargePointSummary>,
  | 'connectors'
  | 'createdDate'
  | 'firmware'
  | 'iccid'
  | 'id'
  | 'model'
  | 'name'
  | 'networkStatus'
  | 'networkStatusUpdatedDate'
  | 'ocppChargePointId'
  | 'referenceId'
  | 'serial'
  | 'updatedDate'
  | 'vendor'
> & {
  activeThisMonth: string;
  allowLongTermOfflineCharging: string;
  connectivityStatus: string;
  flexSolarUnlocked: string;
  localLoadManagementMode: string;
  organisationSlug: string;
  organisationName: string;
  tableData: {
    checked: boolean;
  };
  vendor: string;
};

const activeThisMonth = (chargePoint: Normalised<ChargePointSummary>): string => {
  if (typeof chargePoint.networkStatusUpdatedDate === 'undefined') {
    return 'No';
  }
  return moment(chargePoint.networkStatusUpdatedDate).isAfter(moment().startOf('month')) ? 'Yes' : 'No';
};

const getFlexSolarUnlocked = (chargePoint: Normalised<ChargePoint>): string => {
  if (!chargePoint.model?.startsWith('E2F')) {
    return 'N/A';
  }
  return chargePoint.features?.Solar?.unlocked ? 'Yes' : 'No';
};

const getAllowLongTermOfflineCharging = (chargePoint: Normalised<ChargePoint>): string => {
  if (typeof chargePoint.allowLongTermOfflineCharging === 'undefined') {
    return 'Unknown';
  }
  return chargePoint.allowLongTermOfflineCharging ? 'Yes' : 'No';
};

export const constructTableChargePoints = (chargePoints: Normalised<ChargePoint>[]): TableChargePoint[] =>
  chargePoints.map((chargePoint) => {
    const { networkStatus, networkStatusUpdatedDate, organisation, vendor } = chargePoint;
    const orgHasAttributes = organisationHasAttributes(organisation);
    return {
      ...chargePoint,
      activeThisMonth: activeThisMonth(chargePoint),
      allowLongTermOfflineCharging: getAllowLongTermOfflineCharging(chargePoint),
      connectivityStatus: getChargePointConnectivityStatus(networkStatus, networkStatusUpdatedDate),
      flexSolarUnlocked: getFlexSolarUnlocked(chargePoint),
      localLoadManagementMode: getLocalLoadManagementMode(chargePoint?.llm?.mode),
      organisationName: orgHasAttributes ? organisation.name : '',
      organisationSlug: orgHasAttributes ? organisation.slug : '',
      tableData: {
        checked: false,
      },
      vendor: vendor ?? 'Unknown',
    };
  });

export function selectedChargePoints(chargePoints: TableChargePoint[]): ChargePointState[] {
  const selected = chargePoints.filter((chargePoint) => chargePoint.tableData.checked);
  return selected.map<ChargePointState>((chargePoint) => ({
    id: chargePoint.id,
    name: chargePoint.name,
    ocppChargePointId: chargePoint.ocppChargePointId,
    serial: chargePoint.serial,
    vendor: chargePoint.vendor,
  }));
}
